/**
 * User WebSocket Sync Manager
 *
 * Handles WebSocket connection to /ws/user for real-time User CRDT sync.
 * Similar to SyncManager but simplified for user data (vault index, preferences).
 *
 * Key differences from document SyncManager:
 * - Uses master key encryption (not per-document DEK)
 * - Connects to /ws/user endpoint (always connected when logged in)
 * - Simpler compaction strategy (user data changes less frequently)
 */

import { reportSyncPause, clearSyncPause } from './sync-pause.js';
import { recordQuotaRejection, clearQuotaRejection } from '../services/quota-state.js';
import { isQuotaError } from './server-errors.js';
// Note: We use crypto.subtle directly (not E2EE module) to match the
// backend's expected format: base64(IV + ciphertext)

import { compress, decompress } from "./compression.js";
import * as StorageHealth from "./storage-health.js";
import { isCompactionNeededError, isDurabilityBlockingServerError, isPermissionDeniedError, isTransientServerError } from "./server-errors.js";
import { getSyncUrl } from "../storage/index.js";
import { markConfirmed } from "./unsynced-changes.js";
import { announceSessionEnded, noteConnectFailed, noteConnected } from "./session-check.js";

/**
 * Message types for the user sync protocol
 * Matches the document sync protocol for consistency
 */
export const USER_SYNC_PROTOCOL = 2;

export const UserMessageType = {
  // Client -> Server
  SYNC_STEP_1: "sync-step1",
  DELTA_UPDATE: "delta-update",
  FULL_STATE_UPDATE: "full-state-update",
  HEARTBEAT: "heartbeat",

  // Server -> Client
  READY: "user-sync-ready",
  SYNC_STEP_2: "sync-step2",
  REMOTE_UPDATE: "remote-update",
  COMPACTION_ACK: "compaction-ack",
  DELTA_ACK: "delta-ack",
  HEARTBEAT_ACK: "heartbeat-ack",
  NOTIFICATION: "notification",
  NOTIFICATION_DISMISSED: "notification-dismissed",
  // The server is closing this socket; the close code that follows says
  // what to do (4401: this device's session ended).
  SESSION_INVALIDATED: "session-invalidated",
  ERROR: "error",
};

/**
 * Connection states
 */
export const UserConnectionState = {
  DISCONNECTED: "disconnected",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  SYNCING: "syncing",
  SYNCED: "synced",
  RECONNECTING: "reconnecting",
  ERROR: "error",
};

// Instance counter for debugging
let _userWsInstanceCounter = 0;
const DELTA_ACK_TIMEOUT_MS = 30000;

/**
 * UserSyncManager class
 * Manages WebSocket connection for user data sync
 */
export class UserSyncManager {
  constructor(options = {}) {
    this._instanceId = ++_userWsInstanceCounter;
    // Sent when connecting: the server closes this channel's earlier socket,
    // which is dead if it connects again, instead of counting it.
    this._clientId = crypto.randomUUID();
    // The server refused the last connection: too many for this account (4429).
    this._refusedTooMany = false;

    this.userId = null;
    this.docManager = null; // UserYjsDocManager instance
    this.ws = null;
    this.state = UserConnectionState.DISCONNECTED;

    // Callbacks
    this.onStateChange = options.onStateChange || null;
    this.onError = options.onError || null;
    this.onRemoteUpdate = options.onRemoteUpdate || null;
    this.onNotification = options.onNotification || null;
    this.onNotificationDismissed = options.onNotificationDismissed || null;

    // Reconnection settings
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000;
    this.maxReconnectDelay = 30000; // Max 30 seconds between attempts
    this.reconnectTimer = null;
    this._stopped = false;

    // Offline detection - show "Offline" after this many ms of failing
    this.offlineThresholdMs = 30000; // 30 seconds
    this._firstFailureTime = null; // Timestamp of first connection failure

    // Heartbeat settings
    this.heartbeatInterval = 30000;
    this.heartbeatTimeout = 10000; // 10 seconds to receive ack
    this.heartbeatTimer = null;
    this.heartbeatTimeoutTimer = null;
    this._awaitingHeartbeatAck = false;

    // Offline queue for updates when disconnected
    this.offlineQueue = [];
    this.MAX_OFFLINE_QUEUE_SIZE = 100;

    // Master key for encryption
    this.encryptionKey = null;

    // Document update subscription
    this._unsubscribeDocUpdates = null;

    // Connection promises
    this._disconnectPromise = null;
    this._connectPromise = null;

    // Compaction settings (simpler than document sync)
    this._deltaCount = 0;
    this._lastCompactionTime = 0;
    this._compactionTimer = null;
    this._pendingCompaction = false;
    // A full snapshot may replace server deltas only after a complete read.
    // The generation also invalidates snapshots being encrypted when new
    // server data arrives or the connection changes.
    this._syncComplete = false;
    this._serverProtocolReady = false;
    this._protocolBlocked = false;
    this._protocolTimer = null;
    this._observedPayloads = new Set();
    this._receiveGeneration = 0;
    this._incomingUpdates = 0;
    this._pendingDeltaAcks = new Map();
    this._nextDeltaId = 1;
    this.COMPACTION_INTERVAL_MS = 300000; // 5 minutes (less frequent than docs)
    this.MAX_DELTAS_BEFORE_COMPACT = 50;
  }

  // ==================== Encryption ====================

  /**
   * Set the master key for encryption
   * @param {CryptoKey|Uint8Array|null} key - Master key (CryptoKey or raw bytes)
   */
  async setEncryptionKey(key) {
    if (!key) {
      this.encryptionKey = null;
      console.debug(`[UserSync #${this._instanceId}] Encryption key cleared`);
      return;
    }

    // If already a CryptoKey, use directly
    if (key instanceof CryptoKey) {
      this.encryptionKey = key;
      console.debug(`[UserSync #${this._instanceId}] Encryption key set (CryptoKey)`);
      return;
    }

    // Convert Uint8Array to CryptoKey
    if (key instanceof Uint8Array || ArrayBuffer.isView(key)) {
      this.encryptionKey = await crypto.subtle.importKey(
        "raw",
        key,
        { name: "AES-GCM" },
        false,
        ["encrypt", "decrypt"]
      );
      console.debug(`[UserSync #${this._instanceId}] Encryption key set (imported from bytes)`);
      return;
    }

    throw new Error("Encryption key must be a CryptoKey or Uint8Array");
  }

  /**
   * Encrypt a payload for transmission
   * @param {Uint8Array} data - Data to encrypt
   * @returns {Promise<string>} Base64 encoded encrypted data (IV + ciphertext)
   */
  async _encryptPayload(data) {
    if (!this.encryptionKey) {
      throw new Error(`[UserSync #${this._instanceId}] Cannot send sync data: encryption key not set`);
    }

    // Verify encryption key is a CryptoKey object
    if (!(this.encryptionKey instanceof CryptoKey)) {
      throw new Error("Encryption key is not a valid CryptoKey object");
    }

    // Generate random IV (12 bytes for AES-GCM)
    const iv = crypto.getRandomValues(new Uint8Array(12));

    // Compress before encryption (encrypted data is incompressible)
    const compressed = await compress(data);

    // Encrypt with AES-GCM, using userId as AAD to prevent cross-user replay
    const aad = this.userId ? new TextEncoder().encode(this.userId) : undefined;
    const encrypted = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: iv, ...(aad && { additionalData: aad }) },
      this.encryptionKey,
      compressed
    );

    // Combine IV + ciphertext
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);

    return this._encodeBytes(combined);
  }

  /**
   * Decrypt a payload from the server
   * @param {string} base64Data - Base64 encoded data (IV + ciphertext)
   * @returns {Promise<Uint8Array>}
   */
  async _decryptPayload(base64Data) {
    const data = this._decodeBytes(base64Data);

    if (!this.encryptionKey) {
      throw new Error(`[UserSync #${this._instanceId}] Cannot decrypt sync data: encryption key not set`);
    }

    // Verify encryption key is a CryptoKey object
    if (!(this.encryptionKey instanceof CryptoKey)) {
      throw new Error("Encryption key is not a valid CryptoKey object");
    }

    // Split IV (12 bytes) and ciphertext
    const iv = data.slice(0, 12);
    const ciphertext = data.slice(12);

    // Decrypt with AES-GCM, try with AAD first (new format), then without (legacy)
    if (this.userId) {
      try {
        const aad = new TextEncoder().encode(this.userId);
        const decrypted = await crypto.subtle.decrypt(
          { name: "AES-GCM", iv, additionalData: aad },
          this.encryptionKey,
          ciphertext
        );
        return await decompress(new Uint8Array(decrypted));
      } catch {
        // Fall through to legacy (no-AAD) decryption
      }
    }

    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: iv },
      this.encryptionKey,
      ciphertext
    );

    return await decompress(new Uint8Array(decrypted));
  }

  // ==================== Connection Management ====================

  /**
   * Connect to the user WebSocket
   * @param {string} userId - User ID
   * @param {UserYjsDocManager} docManager - The Yjs doc manager
   */
  async connect(userId, docManager) {
    if (this._connectPromise) {
      console.log(`[UserSync #${this._instanceId}] Connection already in progress`);
      return this._connectPromise;
    }

    this._connectPromise = this._doConnect(userId, docManager);
    try {
      return await this._connectPromise;
    } finally {
      this._connectPromise = null;
    }
  }

  async _doConnect(userId, docManager) {
    this.userId = userId;
    this.docManager = docManager;
    // Until the reconnect step has queued what was changed while
    // disconnected, "nothing pending" does not mean the server has it all.
    this._reconciled = false;
    this._stopped = false;
    this._serverProtocolReady = false;
    this._protocolBlocked = false;
    this._firstFailureTime = null; // Reset failure tracking for new connection
    // Note: reconnectAttempts is reset only on successful connection (onopen)
    // or when triggerReconnect() is called for a fresh start

    this._setState(UserConnectionState.CONNECTING);

    // Storage adapter builds the URL — returns null when sync is disabled
    // in this runtime (e.g. Tauri local-only mode).
    const wsUrl = getSyncUrl(`/ws/user?vaultProtocol=${USER_SYNC_PROTOCOL}&client=${this._clientId}`);
    if (!wsUrl) {
      console.log(`[UserSync #${this._instanceId}] Sync unavailable, skipping connect`);
      this._setState(UserConnectionState.DISCONNECTED);
      return;
    }

    console.log(`[UserSync #${this._instanceId}] Connecting to ${wsUrl}`);

    return new Promise((resolve, reject) => {
      let settled = false;
      // Closed before it opened: a refused session looks like this too (session-check.js).
      let opened = false;

      // Timeout: if WS doesn't open within 5s, resolve anyway
      // (reconnect will keep trying in the background)
      const connectTimeout = setTimeout(() => {
        if (!settled) {
          settled = true;
          console.warn(`[UserSync #${this._instanceId}] Connect timeout (5s), proceeding without sync`);
          resolve();
        }
      }, 5000);

      try {
        this.ws = new WebSocket(wsUrl);
        this.ws.binaryType = "arraybuffer";

        this.ws.onopen = () => {
          console.log(`[UserSync #${this._instanceId}] WebSocket connected`);
          opened = true;
          noteConnected();
          clearTimeout(connectTimeout);
          this._setState(UserConnectionState.CONNECTED);
          // The retry delay resets when the server says it is ready, not
          // here: a refused socket (too many connections) opens, then closes.
          // Old servers accept unknown URL parameters. Require their explicit
          // acknowledgement before sending or applying any vault data.
          clearTimeout(this._protocolTimer);
          this._protocolTimer = setTimeout(() => {
            if (this.ws === socket && !this._serverProtocolReady) this._pauseForProtocol();
          }, 5000);

          if (!settled) {
            settled = true;
            resolve();
          }
        };

        this.ws.onclose = (event) => {
          console.log(
            `[UserSync #${this._instanceId}] WebSocket closed:`,
            event.code,
            event.reason,
          );
          clearTimeout(connectTimeout);
          this._refusedTooMany = event.code === 4429;
          // This device's session ended (signed out elsewhere, password
          // changed): say so (the sign-in dialog). The retries go on: once
          // signed in again here, the next one connects.
          if (event.code === 4401) {
            announceSessionEnded(event.reason);
          } else if (!opened) {
            void noteConnectFailed();
          }
          this._cleanup();

          if (!settled) {
            settled = true;
            resolve(); // Resolve (not reject) so app continues without sync
          }

          if (!this._stopped && !this._protocolBlocked) {
            this._scheduleReconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error(`[UserSync #${this._instanceId}] WebSocket error:`, error);
          clearTimeout(connectTimeout);
          if (this.onError) {
            this.onError(error);
          }
          // Don't reject here — onclose will fire next and handle resolution
        };

        const socket = this.ws;
        let messages = Promise.resolve();
        this.ws.onmessage = (event) => {
          messages = messages.then(async () => {
            if (this.ws === socket && !this._stopped) await this._handleMessage(event);
          });
        };

      } catch (err) {
        clearTimeout(connectTimeout);
        console.error(`[UserSync #${this._instanceId}] Failed to create WebSocket:`, err);
        if (!settled) {
          settled = true;
          reject(err);
        }
      }
    });
  }

  /**
   * Disconnect from the WebSocket
   */
  async disconnect() {
    if (this._disconnectPromise) {
      return this._disconnectPromise;
    }

    // Un-acked vault deltas get a 5 s window before teardown. Still
    // unconfirmed: nothing is lost, they are in the vault's saved copy and
    // its dirty bit stays set; the next connection sends them.
    if (this.ws?.readyState === WebSocket.OPEN && this._pendingDeltaAcks.size > 0 && !StorageHealth.isSigningOut()) {
      if (!await this.flushPendingAcks(5000)) {
        console.warn(`[UserSync #${this._instanceId}] Closing with vault writes not confirmed yet: sent at the next connection`);
      }
    }

    this._stopped = true;
    this._cancelReconnect();
    this._cleanup();

    if (this.ws) {
      const ws = this.ws;
      this.ws = null;

      this._disconnectPromise = new Promise((resolve) => {
        const originalOnClose = ws.onclose;
        ws.onclose = (event) => {
          if (originalOnClose) originalOnClose.call(ws, event);
          resolve();
        };
        ws.close(1000, "Client disconnect");

        // Timeout fallback
        setTimeout(resolve, 1000);
      });

      await this._disconnectPromise;
      this._disconnectPromise = null;
    }

    this._setState(UserConnectionState.DISCONNECTED);
    console.log(`[UserSync #${this._instanceId}] Disconnected`);
  }

  /**
   * Get current connection state
   */
  getState() {
    return this.state;
  }

  // ==================== Internal Methods ====================

  _setState(newState) {
    const oldState = this.state;
    if (oldState === newState) return;

    this.state = newState;
    if (newState === UserConnectionState.SYNCED) this._refusedTooMany = false;
    console.log(`[UserSync #${this._instanceId}] State: ${oldState} → ${newState}`);

    // Notify any pending waitForSync() callers
    this._notifySyncWaiters(newState);

    if (this.onStateChange) {
      this.onStateChange(newState, oldState);
    }
  }

  _cleanup() {
    clearTimeout(this._protocolTimer);
    this._protocolTimer = null;
    this._serverProtocolReady = false;
    this._syncComplete = false;
    this._receiveGeneration++;
    this._pendingCompaction = false;
    this._stopHeartbeat();
    this._stopCompactionTimer();
    if (this._stopped) {
      this._clearPendingDeltaAcks();
    } else if (this._pendingDeltaAcks.size > 0) {
      // The connection dropped (network, server down): what the server had
      // not confirmed is sent again once connected. Nothing is lost; the
      // vault's dirty bit stays set until the server confirms.
      this._clearPendingDeltaAcks();
      if (this.docManager) this._queueOffline(this.docManager.getFullStateForCompaction("connection-closed"));
    }

    if (this._unsubscribeDocUpdates) {
      this._unsubscribeDocUpdates();
      this._unsubscribeDocUpdates = null;
    }
  }

  // ==================== Sync Protocol ====================

  _sendSyncStep1() {
    if (!this._serverProtocolReady) return;
    if (!this.docManager || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    this._syncComplete = false;
    this._receiveGeneration++;
    this._stopCompactionTimer();
    this._setState(UserConnectionState.SYNCING);
    const stateVector = this.docManager.getStateVector();
    const message = {
      type: UserMessageType.SYNC_STEP_1,
      stateVector: this._uint8ArrayToBase64(stateVector),
    };

    this.ws.send(JSON.stringify(message));
    console.log(`[UserSync #${this._instanceId}] Sent sync-step1`);
  }

  async _handleMessage(event) {
    try {
      const message = JSON.parse(event.data);
      if (message.type === UserMessageType.READY) {
        if (message.protocol !== USER_SYNC_PROTOCOL) return this._pauseForProtocol();
        if (this._serverProtocolReady || this._protocolBlocked) return;
        clearTimeout(this._protocolTimer);
        this._protocolTimer = null;
        this._serverProtocolReady = true;
        // Accepted: retries start again from the shortest delay.
        this.reconnectAttempts = 0;
        this._firstFailureTime = null;
        this._startHeartbeat();
        this._subscribeToDocUpdates();
        this._sendSyncStep1();
        return;
      }
      if (!this._serverProtocolReady) return this._pauseForProtocol();

      switch (message.type) {
        case UserMessageType.SYNC_STEP_2:
          await this._handleSyncStep2(message);
          break;

        case UserMessageType.REMOTE_UPDATE:
          await this._handleRemoteUpdate(message);
          break;

        case UserMessageType.COMPACTION_ACK:
          this._handleCompactionAck(message);
          break;

        case UserMessageType.DELTA_ACK:
          this._handleDeltaAck(message.deltaId);
          break;

        case UserMessageType.HEARTBEAT_ACK:
          // Server acknowledges heartbeat - connection is healthy
          this._onHeartbeatAck();
          break;

        case UserMessageType.NOTIFICATION:
          this._handleNotification(message);
          break;

        case UserMessageType.NOTIFICATION_DISMISSED:
          if (this.onNotificationDismissed) {
            this.onNotificationDismissed(message.id);
          }
          break;

        case UserMessageType.ERROR:
          console.error(`[UserSync #${this._instanceId}] Server error:`, message.message);
          if (isQuotaError(message.message)) {
            this._clearPendingDeltaAcks();
            this._pendingCompaction = false;
            this._queueOffline(this.docManager.getFullStateForCompaction('quota-retry'));
            recordQuotaRejection(null, 'vault');
            break;
          }
          if (message.message.includes('Vault changed during compaction; retry sync')) {
            this._pendingCompaction = false;
            this._queueOffline(this.docManager.getFullStateForCompaction('concurrent-sync'));
            this._sendSyncStep1();
            break;
          }
          if (isCompactionNeededError(message.message)) {
            // Too many vault changes since the last compaction: compact now
            // (the snapshot carries the refused change); not a storage failure.
            this._clearPendingDeltaAcks();
            this._pendingCompaction = false;
            void this._triggerCompaction("server-cap");
            break;
          }
          if (isTransientServerError(message.message)) {
            // Rate-limit / too-many-requests: rejected delta is in local Yjs +
            // IndexedDB; clear pending acks so the 30s timeout doesn't lock
            // the gate. State-vector exchange will resync on next reconnect.
            this._clearPendingDeltaAcks();
          } else if (isDurabilityBlockingServerError(message.message) || isPermissionDeniedError(message.message)) {
            StorageHealth.markBroken(`user_server_rejected: ${message.message}`);
          }
          if (this.onError) {
            this.onError(new Error(message.message));
          }
          break;

        case UserMessageType.SESSION_INVALIDATED:
          console.log(`[UserSync #${this._instanceId}] Closed by the server: ${message.reason}`);
          break;

        default:
          console.warn(`[UserSync #${this._instanceId}] Unknown message type:`, message.type);
      }
    } catch (err) {
      console.error(`[UserSync #${this._instanceId}] Error handling message:`, err);
    }
  }

  async _handleSyncStep2(message) {
    if (!this._serverProtocolReady) return this._pauseForProtocol();
    this._syncComplete = false;
    this._observedPayloads.clear();
    const generation = ++this._receiveGeneration;
    this._stopCompactionTimer();
    const hasBaseline = !!message.baseline;
    const deltaCount = message.deltas?.length || 0;
    const hasLegacy = !!message.updates;
    console.log(`[UserSync #${this._instanceId}] Received sync-step2: baseline=${hasBaseline}, deltas=${deltaCount}, legacy=${hasLegacy}`);

    // Track if server had any data
    const serverHadData = !!(message.baseline || (message.deltas && message.deltas.length > 0) || message.updates);
    const serverUpdates = [];
    let decryptedAny = false;
    let baselineFailed = false;
    let updateFailed = false;
    let failureReason = "unreadable";

    // Handle baseline + deltas format.
    // The server omits `baseline` entirely (skip_serializing_if) when only
    // deltas exist, so the presence check must cover both keys — same as the
    // document-path handler (ws-message-handlers.js). Gating on
    // `message.baseline !== undefined` silently dropped deltas-only payloads
    // and pushed users into the bogus "log out and back in" error path.
    if ("deltas" in message || "baseline" in message) {
      if (message.baseline) {
        try {
          const baseline = await this._decryptPayload(message.baseline);
          this.docManager.applyUpdate(baseline);
          serverUpdates.push(baseline);
          await this._rememberPayload(message.baseline);
          decryptedAny = true;
        } catch (err) {
          if (err?.code === "VAULT_STRUCTURE_CONFLICT") failureReason = "structure";
          console.error(`[UserSync #${this._instanceId}] Could not decrypt or apply baseline:`, err);
          baselineFailed = true;
        }
      }

      if (baselineFailed) {
        console.error(`[UserSync #${this._instanceId}] Skipping deltas — deltas without their baseline would produce incomplete state`);
      } else if (message.deltas && message.deltas.length > 0) {
        for (const delta of message.deltas) {
          try {
            const decrypted = await this._decryptPayload(delta);
            this.docManager.applyUpdate(decrypted);
            serverUpdates.push(decrypted);
            await this._rememberPayload(delta);
            decryptedAny = true;
          } catch (err) {
            if (err?.code === "VAULT_STRUCTURE_CONFLICT") failureReason = "structure";
            console.error(`[UserSync #${this._instanceId}] Could not decrypt or apply delta:`, err);
            updateFailed = true;
          }
        }
      }
    } else if (message.updates) {
      // Legacy format
      try {
        const updates = await this._decryptPayload(message.updates);
        this.docManager.applyUpdate(updates);
        serverUpdates.push(updates);
        await this._rememberPayload(message.updates);
        decryptedAny = true;
      } catch (err) {
        if (err?.code === "VAULT_STRUCTURE_CONFLICT") failureReason = "structure";
        console.error(`[UserSync #${this._instanceId}] Could not decrypt or apply updates:`, err);
        updateFailed = true;
      }
    }

    // SAFETY: If the server sent data but we couldn't decrypt ANY of it,
    // or the baseline failed (deltas without their baseline = incomplete
    // vault state), do NOT mark as synced or allow compaction — that would
    // overwrite good server data with our empty/stale local state.
    if (serverHadData && (!decryptedAny || baselineFailed || updateFailed)) {
      const reason = failureReason === "structure"
        ? "Vault initialization histories conflict"
        : baselineFailed
        ? "Baseline could not be decrypted or applied — cannot apply deltas without baseline"
        : "One or more server updates could not be read";
      console.error(`[UserSync #${this._instanceId}] CRITICAL: ${reason}. Not syncing to prevent data loss.`);
      this._markIncompleteSync(failureReason);
      return;
    }

    if (generation !== this._receiveGeneration) return;
    // Only initialize after the server explicitly confirmed an empty vault.
    // Never seed maps before applying existing encrypted remote state.
    if (!serverHadData && this.docManager && !this.docManager.hasData()) {
      this.docManager._initVaultIndexStructure("empty-vault-server-confirmed");
    }
    this._syncComplete = true;
    this._pendingCompaction = false;
    this._deltaCount = 0;
    this._startCompactionTimer();
    this._setState(UserConnectionState.SYNCED);
    clearSyncPause('vault', this.userId);

    // CRDT structure info (counts only, no IDs — avoid leaking doc IDs to console)
    if (this.docManager) {
      const vaultIndex = this.docManager.vaultIndex;
      const docsYMap = vaultIndex.has('docs') ? vaultIndex.get('docs') : null;
      const foldersYMap = vaultIndex.has('folders') ? vaultIndex.get('folders') : null;
      console.log(`[UserSync #${this._instanceId}] Initial sync complete - vaultIndex: ${vaultIndex.size} keys, ${docsYMap?.size || 0} docs, ${foldersYMap?.size || 0} folders`);
    }

    // The in-memory queue is empty after a restart, even when persistence
    // contains edits that never reached the server. Compare complete Yjs states
    // (including deletion history) and publish the validated union when needed.
    if (!this.offlineQueue.length && this.docManager.hasUpdatesNotIn(serverUpdates)) {
      this._queueOffline(this.docManager.getFullStateForCompaction("initial-reconcile"));
    }
    this._reconciled = true;
    this._noteServerHasAll();

    // Flush offline queue
    await this._flushOfflineQueue();
  }

  async _handleRemoteUpdate(message) {
    if (!this._serverProtocolReady) return this._pauseForProtocol();
    if (!message.payload) return;

    this._receiveGeneration++;
    this._incomingUpdates++;
    try {
      const update = await this._decryptPayload(message.payload);
      this.docManager.applyUpdate(update);
      await this._rememberPayload(message.payload);

      if (this.onRemoteUpdate) {
        this.onRemoteUpdate({
          from: message.from,
          timestamp: message.timestamp,
        });
      }
    } catch (err) {
      console.error(`[UserSync #${this._instanceId}] Could not decrypt or apply remote update:`, err);
      this._markIncompleteSync(err?.code === "VAULT_STRUCTURE_CONFLICT" ? "structure" : "unreadable");
    } finally {
      this._incomingUpdates--;
    }
  }

  _pauseForProtocol() {
    this._protocolBlocked = true;
    clearTimeout(this._protocolTimer);
    this._protocolTimer = null;
    this._cancelReconnect();
    this._markIncompleteSync("protocol");
    this.ws?.close?.(4406, "Vault sync requires compatible client and server");
  }

  _markIncompleteSync(reason = "unreadable") {
    reportSyncPause('vault', this.userId, reason);
    this._syncComplete = false;
    this._receiveGeneration++;
    this._pendingCompaction = false;
    this._stopCompactionTimer();
    this._setState(UserConnectionState.ERROR);
    const explanation = reason === "protocol"
      ? "Vault sync paused: update the server and Orbital to compatible versions, then retry. Local data and pending edits are retained."
      : reason === "structure"
      ? "Vault sync paused because initialization histories conflict. Local data and pending edits are retained. Keep local storage for recovery."
      : "Vault sync paused because an update could not be read. Local data and pending edits are retained. Retry sync; do not clear local storage.";
    this.onError?.(new Error(explanation));
  }

  async _rememberPayload(payload) {
    const bytes = Uint8Array.from(atob(payload), char => char.charCodeAt(0));
    const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
    const hash = [...digest].map(byte => byte.toString(16).padStart(2, '0')).join('');
    this._observedPayloads.add(hash);
  }

  _handleCompactionAck(message) {
    clearQuotaRejection(null, 'vault');
    console.log(`[UserSync #${this._instanceId}] Compaction acknowledged`);
    this._pendingCompaction = false;
    this._deltaCount = 0;
    this._lastCompactionTime = Date.now();
    this._noteServerHasAll();
  }

  _handleNotification(message) {
    console.log(`[UserSync #${this._instanceId}] Received notification:`, message.title);

    // Emit event for the notification system to handle
    if (this.onNotification) {
      this.onNotification({
        id: message.id,
        type: message.notification_type,
        title: message.title,
        message: message.message,
        metadata: message.metadata,
        timestamp: message.timestamp,
      });
    }
  }

  // ==================== Sending Updates ====================

  _subscribeToDocUpdates() {
    if (!this.docManager) {
      console.warn(`[UserSync #${this._instanceId}] Cannot subscribe - no docManager`);
      return;
    }

    console.log(`[UserSync #${this._instanceId}] Subscribing to doc updates`);
    this._unsubscribeDocUpdates = this.docManager.onDocUpdate(async (updateInfo) => {
      console.log(`[UserSync #${this._instanceId}] Doc update callback triggered, type=${updateInfo.type}`);
      await this._sendUpdate(updateInfo);
    });
  }

  hasPendingWrites() { return !!(this.offlineQueue.length || this._pendingDeltaAcks.size || this._sendingUpdates || this._pendingCompaction); }

  async _sendUpdate(updateInfo) {
    this._sendingUpdates = (this._sendingUpdates || 0) + 1;
    try { return await this._sendUpdateImpl(updateInfo); }
    finally { this._sendingUpdates--; }
  }

  async _sendUpdateImpl(updateInfo) {
    if (!this.encryptionKey) {
      console.warn(`[UserSync #${this._instanceId}] No encryption key, queuing update`);
      this._queueOffline(updateInfo);
      return;
    }

    if (!this._serverProtocolReady || !this.ws || this.ws.readyState !== WebSocket.OPEN || this.state !== UserConnectionState.SYNCED || !this._syncComplete || this._incomingUpdates > 0) {
      console.warn(`[UserSync #${this._instanceId}] WebSocket not ready (ws=${this.ws?.readyState}, state=${this.state}), queuing update`);
      this._queueOffline(updateInfo);
      return false;
    }

    try {
      // Queued/caller snapshots may predate the last successful merge.
      if (updateInfo.type === "fullState") {
        if (this._observedPayloads.size > 9000) {
          this._queueOffline(updateInfo);
          this._sendSyncStep1();
          return false;
        }
        updateInfo = this.docManager.getFullStateForCompaction(updateInfo.reason);
      }
      const socket = this.ws;
      const generation = this._receiveGeneration;
      // Freeze coverage with the plaintext snapshot. A local delta sent while
      // encryption awaits must not be claimed by this older snapshot.
      const observedPayloads = [...this._observedPayloads];
      const encrypted = await this._encryptPayload(updateInfo.data);
      await this._rememberPayload(encrypted);
      if (socket !== this.ws || socket.readyState !== WebSocket.OPEN ||
          !this._serverProtocolReady || !this._syncComplete || this._incomingUpdates > 0 ||
          generation !== this._receiveGeneration || this.state !== UserConnectionState.SYNCED) {
        this._queueOffline(updateInfo);
        return false;
      }

      if (updateInfo.type === "fullState") {
        // State vector is sent plaintext (base64 encoded) - it's just metadata
        const stateVectorBase64 = updateInfo.stateVector
          ? this._uint8ArrayToBase64(updateInfo.stateVector)
          : undefined;
        this.ws.send(
          JSON.stringify({
            type: UserMessageType.FULL_STATE_UPDATE,
            payload: encrypted,
            observedPayloads,
            stateVector: stateVectorBase64,
            trigger_reason: updateInfo.reason,
          }),
        );
        console.log(`[UserSync #${this._instanceId}] Sent full state (${updateInfo.reason}), size=${updateInfo.data.length} bytes`);
      } else {
        const deltaId = this._createDeltaId();
        this.ws.send(
          JSON.stringify({
            type: UserMessageType.DELTA_UPDATE,
            payload: encrypted,
            deltaId,
          }),
        );
        this._trackDeltaAck(deltaId, "user_delta");
        console.log(`[UserSync #${this._instanceId}] Sent delta #${this._deltaCount + 1}, size=${updateInfo.data.length} bytes`);
        this._deltaCount++;
        this._checkCompactionNeeded();
      }
      return true;
    } catch (err) {
      console.error(`[UserSync #${this._instanceId}] Failed to send update:`, err);
      this._queueOffline(updateInfo);
      return false;
    }
  }

  _createDeltaId() {
    return `user-${this._instanceId}-${Date.now().toString(36)}-${this._nextDeltaId++}`;
  }

  _trackDeltaAck(deltaId, source) {
    const timer = setTimeout(() => this._onDeltaAckLate(deltaId), DELTA_ACK_TIMEOUT_MS);
    this._pendingDeltaAcks.set(deltaId, { timer, source });
  }

  /**
   * A vault write still unconfirmed after DELTA_ACK_TIMEOUT_MS: is the
   * server there? A heartbeat now tells (same rule as the map sockets,
   * websocket.js _onDeltaAckLate): answered while the write is still
   * unconfirmed, storage broken; not answered, the connection is dead, it
   * closes and the write is sent again once connected.
   */
  _onDeltaAckLate(deltaId) {
    const pending = this._pendingDeltaAcks.get(deltaId);
    if (!pending) return;
    console.warn(`[UserSync #${this._instanceId}] Delta ack late, asking the server:`, deltaId, pending.source);
    pending.late = true;
    this._sendHeartbeat();
  }

  _handleDeltaAck(deltaId) {
    if (!deltaId) return;
    const pending = this._pendingDeltaAcks.get(deltaId);
    if (!pending) {
      console.debug(`[UserSync #${this._instanceId}] Ignoring unknown delta ack:`, deltaId);
      return;
    }
    clearTimeout(pending.timer);
    this._pendingDeltaAcks.delete(deltaId);
    this._noteServerHasAll();
  }

  /**
   * The server has every change of the vault made on this device: clear its
   * dirty bit (unsynced-changes.js). Only after this connection's reconnect
   * step, and with nothing left to send or confirm.
   */
  _noteServerHasAll() {
    if (this._reconciled && this.docManager?.docId && !this.hasPendingWrites()) markConfirmed(this.docManager.docId);
  }

  _clearPendingDeltaAcks() {
    for (const pending of this._pendingDeltaAcks.values()) {
      clearTimeout(pending.timer);
    }
    this._pendingDeltaAcks.clear();
  }

  /**
   * Wait until all pending user-CRDT delta acks have arrived (or fail with timeout).
   * Used on disconnect so logout / cross-tab user-CRDT teardown doesn't drop
   * un-acked deltas without surfacing it.
   */
  /**
   * Sign-out's last sync: send what is queued, then wait for the server to
   * confirm everything (timeoutMs is a failure bound).
   * @returns {Promise<boolean>} true when nothing is left unconfirmed
   */
  async settlePendingWrites(timeoutMs = 10000) {
    if (this.state === UserConnectionState.SYNCED && this.ws?.readyState === WebSocket.OPEN) await this._flushOfflineQueue();
    await this.flushPendingAcks(timeoutMs);
    return !this.hasPendingWrites();
  }

  async flushPendingAcks(timeoutMs = 5000) {
    if (this._pendingDeltaAcks.size === 0) return true;
    const start = Date.now();
    while (this._pendingDeltaAcks.size > 0 && Date.now() - start < timeoutMs) {
      await new Promise((r) => setTimeout(r, 50));
    }
    return this._pendingDeltaAcks.size === 0;
  }

  // ==================== Offline Queue ====================

  _queueOffline(updateInfo) {
    if (this.offlineQueue.length >= this.MAX_OFFLINE_QUEUE_SIZE) {
      // Compact: replace entire queue with a single full-state snapshot
      // to prevent data loss (matches document SyncManager pattern)
      if (this.docManager) {
        const fullState = this.docManager.getFullStateForCompaction("offline-queue-compact");
        if (fullState) {
          this.offlineQueue = [fullState];
          console.log(`[UserSync #${this._instanceId}] Offline queue compacted to single full state`);
          return;
        }
      }
      // Without a document snapshot, retain every pending update.
    }
    this.offlineQueue.push(updateInfo);
    console.log(
      `[UserSync #${this._instanceId}] Queued offline update, queue size:`,
      this.offlineQueue.length,
    );
  }

  async _flushOfflineQueue() {
    if (this.offlineQueue.length === 0) return;

    console.log(
      `[UserSync #${this._instanceId}] Flushing ${this.offlineQueue.length} offline updates`,
    );

    // Send full state instead of individual updates for efficiency
    const queuedCount = this.offlineQueue.length;
    const fullState = this.docManager.getFullStateForCompaction("offline-flush");
    if (await this._sendUpdate(fullState)) this.offlineQueue.splice(0, queuedCount);
  }

  // ==================== Compaction ====================

  _startCompactionTimer() {
    this._stopCompactionTimer();
    this._lastCompactionTime = Date.now();

    this._compactionTimer = setInterval(() => {
      // Only compact when there is something new to say — mirrors the
      // doc-level compactor's deltaCount gate (ws-compaction.js). Without
      // this, every open tab re-encrypted and uploaded the entire vault
      // index every 5 minutes forever.
      if (this._deltaCount > 0) {
        this._triggerCompaction("periodic");
      }
    }, this.COMPACTION_INTERVAL_MS);
  }

  _stopCompactionTimer() {
    if (this._compactionTimer) {
      clearInterval(this._compactionTimer);
      this._compactionTimer = null;
    }
  }

  _checkCompactionNeeded() {
    if (this._deltaCount >= this.MAX_DELTAS_BEFORE_COMPACT) {
      this._triggerCompaction("delta-threshold");
    }
  }

  async _triggerCompaction(reason) {
    if (this._pendingCompaction) return;
    if (!this._syncComplete || this._incomingUpdates > 0 || this.state !== UserConnectionState.SYNCED) return;
    if (!this.docManager || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    this._pendingCompaction = true;
    const fullState = this.docManager.getFullStateForCompaction(reason);
    if (!await this._sendUpdate(fullState)) this._pendingCompaction = false;
  }

  // ==================== Heartbeat ====================

  _startHeartbeat() {
    this._stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this._sendHeartbeat();
    }, this.heartbeatInterval);
  }

  /**
   * Send a heartbeat and start timeout timer
   */
  _sendHeartbeat() {
    if (this._awaitingHeartbeatAck) {
      // Previous heartbeat didn't get ack - connection is dead
      console.log(`[UserSync #${this._instanceId}] Heartbeat timeout - no ack received, connection dead`);
      this._handleHeartbeatTimeout();
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: UserMessageType.HEARTBEAT }));
      this._awaitingHeartbeatAck = true;

      // Set timeout for ack
      this.heartbeatTimeoutTimer = setTimeout(() => {
        if (this._awaitingHeartbeatAck) {
          console.log(`[UserSync #${this._instanceId}] Heartbeat timeout - connection appears dead`);
          this._handleHeartbeatTimeout();
        }
      }, this.heartbeatTimeout);
    }
  }

  /**
   * Handle heartbeat ack received from server
   */
  _onHeartbeatAck() {
    this._awaitingHeartbeatAck = false;
    if (this.heartbeatTimeoutTimer) {
      clearTimeout(this.heartbeatTimeoutTimer);
      this.heartbeatTimeoutTimer = null;
    }
    // The server answers, yet did not confirm a late write: it did not store it.
    const late = [...this._pendingDeltaAcks.entries()].filter(([, pending]) => pending.late);
    if (!late.length) return;
    for (const [deltaId] of late) this._pendingDeltaAcks.delete(deltaId);
    console.error(`[UserSync #${this._instanceId}] Server answers but did not confirm:`, late.map(([id]) => id));
    StorageHealth.markBroken(`user_delta_ack_timeout: ${late.map(([, pending]) => pending.source).join(", ")}`);
  }

  /**
   * Handle heartbeat timeout - connection is dead
   * Immediately starts reconnection instead of waiting for TCP close timeout
   */
  _handleHeartbeatTimeout() {
    this._awaitingHeartbeatAck = false;
    this._stopHeartbeat();

    if (this.ws) {
      // Detach handlers to prevent stale onclose from interfering with reconnection
      // TCP close can take 60+ seconds when network is down
      const oldWs = this.ws;
      this.ws = null;

      oldWs.onclose = null;
      oldWs.onerror = null;
      oldWs.onmessage = null;

      if (oldWs.readyState === WebSocket.OPEN || oldWs.readyState === WebSocket.CONNECTING) {
        console.log(`[UserSync #${this._instanceId}] Forcing WebSocket close due to heartbeat timeout`);
        oldWs.close(4000, "Heartbeat timeout");
      }
    }

    // Don't wait for onclose - immediately start reconnecting
    this._cleanup();
    this._scheduleReconnect();
  }

  _stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.heartbeatTimeoutTimer) {
      clearTimeout(this.heartbeatTimeoutTimer);
      this.heartbeatTimeoutTimer = null;
    }
    this._awaitingHeartbeatAck = false;
  }

  // ==================== Reconnection ====================

  /**
   * Schedule a reconnection attempt
   * Never gives up - keeps trying forever at maxReconnectDelay interval
   * Shows DISCONNECTED state after offlineThresholdMs to indicate offline status
   */
  _scheduleReconnect() {
    // Prevent double-scheduling
    if (this._stopped || this._protocolBlocked || this.reconnectTimer) return;

    // Track when failures started
    if (this._firstFailureTime === null) {
      this._firstFailureTime = Date.now();
    }

    // After threshold, show DISCONNECTED but keep trying
    const failingForMs = Date.now() - this._firstFailureTime;
    if (failingForMs >= this.offlineThresholdMs) {
      // Show offline state but continue reconnecting
      if (this.state !== UserConnectionState.DISCONNECTED) {
        console.log(`[UserSync #${this._instanceId}] Offline threshold reached, showing offline status`);
        this._setState(UserConnectionState.DISCONNECTED);
      }
    } else {
      // Still within threshold, show reconnecting
      this._setState(UserConnectionState.RECONNECTING);
    }

    // Exponential backoff with jitter, capped at maxReconnectDelay
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts) +
        Math.random() * 1000,
      this.maxReconnectDelay,
    );

    console.log(
      `[UserSync #${this._instanceId}] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})`,
    );

    this.reconnectTimer = setTimeout(async () => {
      this.reconnectTimer = null;
      this.reconnectAttempts++;

      try {
        await this._doConnect(this.userId, this.docManager);
      } catch (err) {
        console.error(`[UserSync #${this._instanceId}] Reconnect failed:`, err);
        this._scheduleReconnect();
      }
    }, delay);
  }

  _cancelReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Trigger an immediate reconnection attempt.
   * Resets the attempt counter and skips backoff delay.
   * Use this when network connectivity is restored.
   */
  triggerReconnect() {
    // Don't reconnect if explicitly stopped or not initialized
    if (this._stopped || !this.userId) {
      console.log(`[UserSync #${this._instanceId}] triggerReconnect skipped (stopped or no user)`);
      return;
    }

    // Already connected or connecting
    if (this.state === UserConnectionState.CONNECTED ||
        this.state === UserConnectionState.SYNCING ||
        this.state === UserConnectionState.SYNCED ||
        this.state === UserConnectionState.CONNECTING) {
      console.log(`[UserSync #${this._instanceId}] triggerReconnect skipped (already connected/connecting)`);
      return;
    }

    console.log(`[UserSync #${this._instanceId}] triggerReconnect - resetting attempts and reconnecting immediately`);

    // Cancel any pending backoff timer
    this._cancelReconnect();

    // Reset for fresh start
    this.reconnectAttempts = 0;
    this._firstFailureTime = null;

    // Immediately try to connect
    this._doConnect(this.userId, this.docManager).catch(() => {
      // If immediate attempt fails, fall back to normal backoff
      this._scheduleReconnect();
    });
  }

  /**
   * Verify the connection is alive after a tab-resume signal.
   * See SyncManager.probeConnection in websocket.js for full rationale —
   * mobile background suspension can leave a zombie OPEN socket that the
   * regular heartbeat interval needs ~40s to notice.
   */
  /** Whether the server refused the last connection: too many for this account. */
  isRefusedTooMany() {
    return this._refusedTooMany;
  }

  probeConnection() {
    if (this._stopped || !this.userId) return;

    if (this.state === UserConnectionState.SYNCED ||
        this.state === UserConnectionState.SYNCING ||
        this.state === UserConnectionState.CONNECTED) {
      this._sendHeartbeat();
      return;
    }

    if (this.state === UserConnectionState.DISCONNECTED ||
        this.state === UserConnectionState.RECONNECTING) {
      this.triggerReconnect();
    }
  }

  // ==================== Utility ====================

  /**
   * Encode bytes to base64
   * @param {Uint8Array} bytes
   * @returns {string}
   */
  _encodeBytes(bytes) {
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Decode base64 to bytes
   * @param {string} base64
   * @returns {Uint8Array}
   */
  _decodeBytes(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Convert Uint8Array to base64 (alias for state vector encoding)
   */
  _uint8ArrayToBase64(uint8Array) {
    return this._encodeBytes(uint8Array);
  }

  /**
   * Wait for connection to reach synced state
   * @param {number} timeout - Max time to wait in ms
   * @returns {Promise<boolean>}
   */
  /**
   * Wait for connection to reach synced state.
   * Uses a waiter-array pattern (concurrent-safe — multiple callers supported).
   * @param {number} timeout - Max time to wait in ms
   * @returns {Promise<boolean>}
   */
  waitForSync(timeout = 10000) {
    return new Promise((resolve) => {
      if (this.state === UserConnectionState.SYNCED) {
        resolve(true);
        return;
      }

      if (!this._syncWaiters) this._syncWaiters = [];
      const entry = { resolve, resolved: false };
      this._syncWaiters.push(entry);

      entry.timer = setTimeout(() => {
        if (!entry.resolved) {
          entry.resolved = true;
          this._syncWaiters = this._syncWaiters.filter((w) => w !== entry);
          console.warn(`[UserSync #${this._instanceId}] waitForSync timed out`);
          resolve(false);
        }
      }, timeout);
    });
  }

  /**
   * Notify all pending sync waiters (called from _setState)
   * @param {string} newState
   */
  _notifySyncWaiters(newState) {
    if (!this._syncWaiters || this._syncWaiters.length === 0) return;
    const success =
      newState === UserConnectionState.SYNCED;
    const terminal =
      success ||
      newState === UserConnectionState.ERROR ||
      newState === UserConnectionState.DISCONNECTED;
    if (!terminal) return;

    const waiters = this._syncWaiters;
    this._syncWaiters = [];
    for (const entry of waiters) {
      if (!entry.resolved) {
        entry.resolved = true;
        clearTimeout(entry.timer);
        entry.resolve(success);
      }
    }
  }
}
